import { useNavigate } from 'react-router-dom'

import { Box, Icon, Section, Text } from '@factorialco/deprecated-design-system'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import canSeeIntegration from 'modules/marketplace/lib/canSeeIntegration'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'
import { CompanyInstallations } from 'modules/marketplace/store/collections/companyInstallations'

interface IntegrationBoxProps {
  linkName: string
  destinationPath: string
  isLastElement?: boolean
}

const IntegrationBox = ({ destinationPath, linkName, isLastElement }: IntegrationBoxProps) => {
  const navigate = useNavigate()

  return (
    <Box
      cursor="pointer"
      onClick={() => navigate(destinationPath)}
      border={
        !isLastElement
          ? {
              bottom: {
                color: 'grey400',
                width: 's1',
                style: 'solid',
              },
            }
          : {}
      }
      gap="s8"
      flexDirection="row"
      alignItems="center"
      justifyContent="spaceBetween"
      padding="s16"
    >
      <Text.Normal color="grey1000" weight="normal">
        {linkName}
      </Text.Normal>
      <Box
        padding="s2"
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        borderRadius={{
          all: 'rel500',
        }}
      >
        <Icon.Medium icon="arrow-right" />
      </Box>
    </Box>
  )
}

interface ContentProps {
  installedIntegrations: CompanyInstallations
}

const Content = ({ installedIntegrations }: ContentProps) => {
  const { navigation } = useNavigation()

  const hasA3nomInstalled = () => {
    return canSeeIntegration(integrationCodes.a3nom, installedIntegrations)
  }

  const hasDatevInstalled = () => {
    return canSeeIntegration(integrationCodes.datev, installedIntegrations)
  }

  const hasDatevApiInstalled = () => {
    return canSeeIntegration(integrationCodes.datev_api, installedIntegrations)
  }

  const hasDatevLugApiInstalled = () => {
    return canSeeIntegration(integrationCodes.datev_lug_api, installedIntegrations)
  }

  const hasZucchettiInstalled = () => {
    return canSeeIntegration(integrationCodes.zucchetti, installedIntegrations)
  }

  const hasPaierhInstalled = () => {
    return canSeeIntegration(integrationCodes.paierh, installedIntegrations)
  }
  const hasYeapPaierhInstalled = () => {
    return canSeeIntegration(integrationCodes.yeap_paierh, installedIntegrations)
  }

  const hasSilaeInstalled = () => {
    return canSeeIntegration(integrationCodes.silae, installedIntegrations)
  }

  const hasSilaeApiInstalled = () => {
    return canSeeIntegration(integrationCodes.silae_api, installedIntegrations)
  }

  const hasGispagheInstalled = () => {
    return canSeeIntegration(integrationCodes.gispaghe, installedIntegrations)
  }

  const integrations = [
    {
      check: hasA3nomInstalled,
      linkName: i18n.t('app_store_v2.app_integrations.payroll_integrations_a3nom.general.title'),
      destinationPath: navigation.settings.payroll_bundle.integrationA3nom.path,
    },
    {
      check: hasDatevInstalled,
      linkName: i18n.t('app_store_v2.app_integrations.payroll_integrations_datev.general.title'),
      destinationPath: navigation.settings.payroll_bundle.integrationDatev.path,
    },
    {
      check: () => hasDatevApiInstalled() || hasDatevLugApiInstalled(),
      linkName: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_datev_api.general.title'
      ),
      destinationPath: navigation.settings.payroll_bundle.integrationDatevApi.path,
    },
    {
      check: hasZucchettiInstalled,
      linkName: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_zucchetti.general.title'
      ),
      destinationPath: navigation.settings.payroll_bundle.integrationZucchetti.path,
    },
    {
      check: hasPaierhInstalled,
      linkName: i18n.t('app_store_v2.app_integrations.payroll_integrations_paierh.general.title'),
      destinationPath: navigation.settings.payroll_bundle.integrationPaierh.path,
    },
    {
      check: hasYeapPaierhInstalled,
      linkName: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_yeap_paierh.general.title'
      ),
      destinationPath: navigation.settings.payroll_bundle.integrationYeapPaierh.path,
    },
    {
      check: hasSilaeInstalled,
      linkName: i18n.t('app_store_v2.app_integrations.payroll_integrations_silae.general.title'),
      destinationPath: navigation.settings.payroll_bundle.integrationSilae.path,
    },
    {
      check: hasSilaeApiInstalled,
      linkName: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_silae_api.general.title'
      ),
      destinationPath: navigation.settings.payroll_bundle.integrationSilaeApi.path,
    },
    {
      check: hasGispagheInstalled,
      linkName: i18n.t('app_store_v2.app_integrations.payroll_integrations_gispaghe.general.title'),
      destinationPath: navigation.settings.payroll_bundle.integrationGispaghe.path,
    },
  ]

  const integrationsAvailable = [
    hasA3nomInstalled,
    hasDatevInstalled,
    hasDatevApiInstalled,
    hasDatevLugApiInstalled,
    hasZucchettiInstalled,
    hasPaierhInstalled,
    hasYeapPaierhInstalled,
    hasSilaeInstalled,
    hasSilaeApiInstalled,
    hasGispagheInstalled,
  ]

  const integrationsInstalled = integrationsAvailable.filter((func) => func()).length

  return (
    <Box
      border={
        integrationsInstalled > 0
          ? {
              all: {
                color: 'grey400',
                width: 's1',
                style: 'solid',
              },
            }
          : {}
      }
      borderRadius={{
        all: 'abs016',
      }}
    >
      {integrations.map(({ check, linkName, destinationPath }, index) => {
        const isLastElement = index === integrations.length - 1

        if (check()) {
          return (
            <IntegrationBox
              key={linkName}
              linkName={linkName}
              destinationPath={destinationPath}
              isLastElement={isLastElement}
            />
          )
        }
      })}
    </Box>
  )
}

interface IntegrationsProps {
  installedIntegrations: CompanyInstallations
}

const Integrations = ({ installedIntegrations }: IntegrationsProps) => {
  return (
    <Section
      title={i18n.t('app_store_v2.apps.payroll_bundle.settings.integrations.title')}
      illustration="URL"
      description={i18n.t('app_store_v2.apps.payroll_bundle.settings.integrations.description')}
    >
      <Content installedIntegrations={installedIntegrations} />
    </Section>
  )
}

export default Integrations
